/**
 * Where this shell keeps the Record (D241) — with the account when the
 * player is signed in, on the device when they are not, and the device's
 * merged into the account the first time they are.
 *
 * @remarks
 * Mike's ruling (2026-10-09): the Record is held with the player's account.
 * A free player who is not signed in keeps it here, in `localStorage` (the
 * player-host's key-value store, every touch in `try`), and the first read
 * after they sign in folds the device's layers into the account and clears
 * the device — signing in reloads the page, and buying needs an account, so
 * "the first time they sign in or buy" is the first boot that finds them
 * signed in.
 *
 * The two play modes differ in who reads the account:
 * - **A client-run game** reads and writes the account through the store's
 *   browser doors, and falls back to the device when the store will not
 *   answer — so nothing is lost; it merges on the next signed-in boot.
 * - **A server-run game** never reads the account here: the play Worker
 *   reads it at genesis and keeps what the turns write. The shell's part is
 *   the merge, and the device's Record for a player who is not signed in —
 *   sent with the open, kept from the turns.
 *
 * "Forget the Record" forgets both copies, and throws when the account's
 * did not go (D242): the settings control says the Record is clear only
 * when it is. For a server-run game the account's copy is the play
 * Worker's, and the remote host asks it.
 *
 * @packageDocumentation
 */

import type { RecordLayer } from '@orpheus/content-schema';
import type { RecordStorageAdapter } from '@orpheus/player-host';

import type { RecordDoorsClient } from './record-doors-client.js';

/** What the shell's Record store is built from. */
export interface ShellRecordStorageOptions {
	/** The store's Record doors — absent in a build that names no store Worker, where the device is all there is. */
	readonly account?: RecordDoorsClient;
	/** The device's Record — `localStorage`, through the player-host's key-value store. */
	readonly device: RecordStorageAdapter;
	/** Whether the play Worker holds a signed-in player's Record itself (a server-run game). */
	readonly isHeldByServer: boolean;
	/** Whether a player is signed in right now — asked once per boot; signing in or out reloads the page. */
	readonly isSignedIn: () => Promise<boolean>;
}

/**
 * Fold the device's Record into the account and clear the device — once the
 * store has kept it, and not before.
 *
 * @param device - The device's Record
 * @param account - The store's Record doors
 * @param gameId - The game
 * @returns Nothing
 */
const mergeDeviceIntoAccount = async (device: RecordStorageAdapter, account: NonNullable<ShellRecordStorageOptions['account']>, gameId: string): Promise<void> => {
	const held = await device.readRecord(gameId);

	if (Array.isArray(held) && held.length > 0 && (await account.foldIntoAccountRecord(gameId, held as readonly RecordLayer[]))) {
		await device.forgetRecord(gameId);
	}
};

/**
 * The shell's Record store (see the module remarks).
 *
 * @param options - {@link ShellRecordStorageOptions}
 * @returns The adapter a host is handed
 */
export const createShellRecordStorage = (options: ShellRecordStorageOptions): RecordStorageAdapter => {
	const { account, device, isHeldByServer } = options;
	let signedIn: Promise<boolean> | undefined;
	const isSignedIn = async (): Promise<boolean> => {
		signedIn ??= options.isSignedIn().catch(() => false);

		return signedIn;
	};
	/** The account's doors, when there is an account to use them for. */
	const accountInUse = async (): Promise<ShellRecordStorageOptions['account']> => (account !== undefined && (await isSignedIn()) ? account : undefined);

	return {
		appendLayers: async (gameId, layers) => {
			const inUse = isHeldByServer ? undefined : await accountInUse();

			// Kept with the account when it will take them; otherwise on the device, to merge on a later signed-in boot.
			if (inUse === undefined || !(await inUse.foldIntoAccountRecord(gameId, layers))) {
				await device.appendLayers(gameId, layers);
			}
		},
		// D242: the device's copy, and a client-run game's account copy — which must be confirmed, because the
		// control is about to say the Record is clear. A server-run game's account copy is the play Worker's to forget
		// (the remote host asks it).
		forgetRecord: async (gameId) => {
			await device.forgetRecord(gameId);

			const inUse = isHeldByServer ? undefined : await accountInUse();

			if (inUse !== undefined && !(await inUse.forgetAccountRecord(gameId))) {
				throw new Error('the store did not forget the account\'s Record');
			}
		},
		readRecord: async (gameId) => {
			const inUse = await accountInUse();

			if (inUse === undefined) {
				return device.readRecord(gameId);
			}

			await mergeDeviceIntoAccount(device, inUse, gameId);

			if (isHeldByServer) {
				// The play Worker reads the account at genesis; a device that sent nothing sends nothing it would ignore.
				return [];
			}

			return (await inUse.accountRecord(gameId)) ?? device.readRecord(gameId);
		},
	};
};
